import type { ReactNode } from "react";
import ScheduleTemplatesList from "@/pages/human-resources/time-management/components/schedule-templates/ScheduleTemplatesList.tsx";
import DailyTagsList from "@/pages/human-resources/time-management/components/daily-tags/DailyTagsList.tsx";
import PositionsList from "@/pages/human-resources/time-management/components/schedule-palette/positions/PositionsList.tsx";
import useScheduleViewState from "@/pages/human-resources/time-management/model/schedule-view/useScheduleViewState.ts";
import useScheduleViewStateDispatch from "@/pages/human-resources/time-management/model/schedule-view/useScheduleViewStateDispatch.ts";
import type { ScheduleTemplate } from "../../business/schedule-template/ScheduleTemplate.ts";
import type { DailyTag } from "@/pages/human-resources/time-management/business/daily-tag/DailyTag.ts";
import { useTranslation } from "react-i18next";
import useScheduleReferenceData from "@/pages/human-resources/time-management/schedules/reference-provider/useScheduleReferenceData.ts";

interface Properties {
  searchInput: string;
  horizontal: boolean;
}

export default function TabSwitcher(props: Properties): ReactNode {
  const { t } = useTranslation("humanResources", {
    keyPrefix: "timeManagement",
  });

  const { palette } = useScheduleViewState();
  const { dispatch } = useScheduleViewStateDispatch();
  const referenceData = useScheduleReferenceData();

  function pickTemplate(selected: ScheduleTemplate): void {
    const sameSelected = selected === palette.template;
    dispatch({
      type: "pickScheduleTemplate",
      template: sameSelected ? undefined : selected,
    });
  }

  function pickDailyTag(selected: DailyTag): void {
    const sameSelected = selected === palette.dailyTag;
    dispatch({
      type: "pickDailyTag",
      dailyTag: sameSelected ? undefined : selected,
    });
  }

  switch (palette.view) {
    case "templates":
      return (
        <ScheduleTemplatesList
          scheduleTemplates={referenceData.listTemplates()}
          horizontal={props.horizontal}
          searchInput={props.searchInput}
          selectedTemplate={palette.template}
          onSelectTemplate={pickTemplate}
          helpText={t("help.pickScheduleTemplate")}
        />
      );
    case "tags":
      return (
        <DailyTagsList
          dailyTags={referenceData.listDailyTags()}
          horizontal={props.horizontal}
          searchInput={props.searchInput}
          selectedDailyTag={palette.dailyTag}
          onSelectDailyTag={pickDailyTag}
          helpText={t("help.pickDailyTag")}
        />
      );
    case "positions":
      return (
        <PositionsList
          positions={referenceData.listEnabledPositions()}
          horizontal={props.horizontal}
          searchInput={props.searchInput}
        />
      );
  }
}
